import React, { useState, useEffect, useCallback } from "react";
import {
  SearchIcon,
  ShieldIcon,
  TrashIcon,
  PlusIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import api from "../services/api";

interface UserItem {
  id: number;
  username: string;
  email: string | null;
  full_name: string | null;
}

interface RoleAssignment {
  id: string;
  user_id: number;
  role_id: string;
  role_name: string | null;
  scope_type: string;
  scope_id: string | null;
  assigned_by: string | null;
  assigned_at: string;
}

interface RoleOption {
  id: string;
  name: string;
  is_system_role: boolean;
}

interface GroupOption {
  id: string;
  name: string;
  parent_id: string | null;
}

export const Users: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignRoleId, setAssignRoleId] = useState("");
  const [assignScopeType, setAssignScopeType] = useState("organization");
  const [assignGroupId, setAssignGroupId] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await api.getUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRolesAndGroups = useCallback(async () => {
    try {
      const [r, g] = await Promise.all([api.getRoles(), api.getGroups()]);
      setRoles(r);
      setGroups(g);
    } catch (err) {
      console.error("Failed to fetch roles/groups:", err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchRolesAndGroups();
  }, [fetchUsers, fetchRolesAndGroups]);

  // Filter users locally as the user types
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredUsers(
      users.filter(
        (u) =>
          u.username.toLowerCase().includes(q) ||
          (u.email && u.email.toLowerCase().includes(q)) ||
          (u.full_name && u.full_name.toLowerCase().includes(q))
      )
    );
  }, [searchQuery, users]);

  const fetchAssignments = useCallback(async (userId: number) => {
    try {
      const data = await api.getUserRoles(userId);
      setAssignments(data);
    } catch (err) {
      console.error("Failed to fetch user roles:", err);
    }
  }, []);

  useEffect(() => {
    if (selectedUserId) fetchAssignments(selectedUserId);
    else setAssignments([]);
  }, [selectedUserId, fetchAssignments]);

  const handleAssignRole = async () => {
    if (!selectedUserId || !assignRoleId) return;
    try {
      await api.assignUserRole(selectedUserId, {
        role_id: assignRoleId,
        scope_type: assignScopeType,
        scope_id:
          assignScopeType === "group" && assignGroupId
            ? assignGroupId
            : undefined,
      });
      setShowAssignModal(false);
      setAssignRoleId("");
      setAssignScopeType("organization");
      setAssignGroupId("");
      fetchAssignments(selectedUserId);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to assign role");
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!selectedUserId) return;
    if (!window.confirm("Remove this role assignment?")) return;
    try {
      await api.removeUserRole(selectedUserId, assignmentId);
      fetchAssignments(selectedUserId);
    } catch (err) {
      console.error("Failed to remove role:", err);
    }
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);

  const resolveGroupName = (groupId: string | null) => {
    if (!groupId) return "Organization";
    const g = groups.find((gr) => gr.id === groupId);
    return g ? g.name : groupId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        User Management
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User list */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter users..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-[600px]">
            {filteredUsers.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                No users found.
              </p>
            ) : (
              filteredUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setSelectedUserId(u.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700 transition-colors ${
                    selectedUserId === u.id
                      ? "bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {u.username}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {u.email || "No email"}
                    {u.full_name && ` - ${u.full_name}`}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* User detail + role assignments */}
        <div className="lg:col-span-2 space-y-6">
          {selectedUser ? (
            <>
              {/* User info */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedUser.username}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedUser.email || "No email"}
                      {selectedUser.full_name &&
                        ` - ${selectedUser.full_name}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Role assignments */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <ShieldIcon className="h-4 w-4 inline mr-1" />
                    Role Assignments ({assignments.length})
                  </h3>
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Assign Role
                  </button>
                </div>

                {assignments.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No roles assigned. This user has no RBAC permissions.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {assignments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 rounded-md"
                      >
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {a.role_name || a.role_id}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                                a.scope_type === "organization"
                                  ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                  : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              }`}
                            >
                              {a.scope_type === "organization"
                                ? "Org-wide"
                                : resolveGroupName(a.scope_id)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {a.assigned_by && `Assigned by ${a.assigned_by}`}
                            {a.assigned_at &&
                              ` on ${new Date(a.assigned_at).toLocaleDateString()}`}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveAssignment(a.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Groups section — derived from role assignments */}
              {(() => {
                const groupAssignments = assignments.filter(
                  (a) => a.scope_type === "group" && a.scope_id
                );
                const uniqueGroupIds = Array.from(new Set(groupAssignments.map((a) => a.scope_id!)));
                if (uniqueGroupIds.length === 0) return null;
                return (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                      <UsersIcon className="h-4 w-4 inline mr-1" />
                      Groups ({uniqueGroupIds.length})
                    </h3>
                    <div className="space-y-2">
                      {uniqueGroupIds.map((gid) => {
                        const groupName = resolveGroupName(gid);
                        const rolesInGroup = groupAssignments
                          .filter((a) => a.scope_id === gid)
                          .map((a) => a.role_name || a.role_id);
                        return (
                          <div
                            key={gid}
                            className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-900 rounded-md"
                          >
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {groupName}
                            </span>
                            <div className="flex gap-1.5">
                              {rolesInGroup.map((rn, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                >
                                  {rn}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center border border-gray-200 dark:border-gray-700">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Select a user from the list to view and manage their role
                assignments.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Assign Role Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Assign Role to {selectedUser?.username}
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Role selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={assignRoleId}
                  onChange={(e) => setAssignRoleId(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                >
                  <option value="">Select a role...</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                      {r.is_system_role ? " (system)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scope type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Scope
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="scopeType"
                      value="organization"
                      checked={assignScopeType === "organization"}
                      onChange={(e) => setAssignScopeType(e.target.value)}
                      className="mr-2 text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Organization (all groups)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="scopeType"
                      value="group"
                      checked={assignScopeType === "group"}
                      onChange={(e) => setAssignScopeType(e.target.value)}
                      className="mr-2 text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Specific group
                    </span>
                  </label>
                </div>
              </div>

              {/* Group selector (only when scope=group) */}
              {assignScopeType === "group" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Group
                  </label>
                  <select
                    value={assignGroupId}
                    onChange={(e) => setAssignGroupId(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                  >
                    <option value="">Select a group...</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRole}
                disabled={
                  !assignRoleId ||
                  (assignScopeType === "group" && !assignGroupId)
                }
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
